import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, Injectable, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn, ReactiveFormsModule, FormsModule, FormControl } from '@angular/forms';
import { QnSbnService } from '../services/qn-sbn-service.service';
import { OneSnackBarService, OneSnackBarType, OneSnackBarModule } from '@one/angular-kit/modal';
import * as XLSX from 'xlsx';
import { OneWebComponentsAngularModule } from '@one/angular';
import { formatDate, CommonModule } from '@angular/common';
import { EmailDeliveryTableComponent } from '../email-delivery-table/email-delivery-table.component';
import { FileFormatDialogComponent } from '../notification/file-format-dialog/file-format-dialog.component';
import { SnackbarService } from '../services/snackbar.service';



function dateRangeValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const from = control.get('dateFrom')?.value;
    const to = control.get('dateTo')?.value;
    if (from && to && from > to) {
      return { dateRangeInvalid: true };
    }
    return null;
  };
}

function formatDateOnly(dateValue: Date): string {
  if (dateValue) {
    const date = new Date(dateValue);
    const day = date.toLocaleString('default', { day: '2-digit' });
    const month = date.toLocaleString('default', { month: '2-digit' });
    const year = date.toLocaleString('default', { year: 'numeric' });
    return year + '-' + month + '-' + day;
  } else {
    return '';
  }
}

@Component({
  selector: 'dl-email-delivery',
  standalone: true,
  imports: [OneWebComponentsAngularModule, CommonModule, ReactiveFormsModule, FormsModule, EmailDeliveryTableComponent, OneSnackBarModule, FileFormatDialogComponent],
  templateUrl: './email-delivery.component.html',
  styleUrls: ['./email-delivery.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EmailDeliveryComponent implements OnInit {

  @ViewChild('delivered') delivered: ElementRef;
  @ViewChild('failed') failed: ElementRef;
  deliveredPercent = 0;
  failedPercent = 0;
  deliveredTitle = `Delivered: ${this.deliveredPercent}%`
  failedTitle = `Failed: ${this.failedPercent}%`
  form: FormGroup;
  deliveryStatuses = ['Delivered', 'Failed'];
  countries = [];
  userCountries = [];
  emailChartTableData;
  emailTableData = [];
  accountInput = [];
  notificationInput = [];
  downloadVisible: boolean = false;
  
  // FormControl getters for template binding
  get dateFrom(): FormControl {
    return this.form.get('dateFrom') as FormControl;
  }

  get dateTo(): FormControl {
    return this.form.get('dateTo') as FormControl;
  }

  get account(): FormControl {
    return this.form.get('account') as FormControl;
  }

  get country(): FormControl {
    return this.form.get('country') as FormControl;
  }

  get deliveryStatus(): FormControl {
    return this.form.get('deliveryStatus') as FormControl;
  }

  disabledStatusOptions: Set<string> = new Set();

  constructor(
    private fb: FormBuilder,
    public qnsbnService: QnSbnService,
    private snackBarNotification: SnackbarService,
    public cdr: ChangeDetectorRef
  ){}

  ngOnInit() {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    // Initialize with the same countries as unique-id-dialog
    const predefinedCountries = [
      "GB", "MT", "IL", "AT", "ES", "AR", 
      "PT", "CA", "HU", "MY", "BE", "PH"
    ];
    
    // Get user countries or fallback to predefined list
    this.userCountries = this.qnsbnService.getUserCountries();
    if (!this.userCountries || this.userCountries.length === 0) {
      this.userCountries = predefinedCountries;
    }
    
    this.countries = [...this.userCountries ]
    this.form = this.fb.group(
      {
        country: [[...this.userCountries], Validators.required],
       notification: [''],
        account: [''],
        deliveryStatus: [['Failed'], Validators.required],
        dateFrom: [thirtyDaysAgo, Validators.required],
        dateTo: [today, Validators.required]
      },
      { validators: dateRangeValidator() }
    );

    this.search();
  }

  clearDate(controlName: string) {
    this.form.get(controlName)?.setValue(null);
  }

  clearAccount() {
    this.form.get('account')?.setValue('');
  }

  countrySelection(opts){
    if(opts.includes('All')){
      return this.userCountries
    }else{
      return opts
    }
  }

  search() {
    if (this.form.valid) {
       if (this.form.get('account').value.includes(",")) {
        this.accountInput = [...new Set(
          this.form.get('account').value.split(",").map(i => i.trim()).filter(i => i.length > 0)
        )];
      } else {
        this.accountInput = [this.form.get('account').value.trim()];
      }
      if (this.form.get('notification').value.includes(",")) {
        this.notificationInput = [...new Set(
          this.form.get('notification').value.split(",").map(i => i.trim()).filter(i => i.length > 0)
        )];
      } else {
        this.notificationInput = [this.form.get('notification').value.trim()];
      }

      const searchObj = {
        "Country": this.countrySelection(this.form.get('country').value),
        "NotificationSearch": this.notificationInput,
        "AccountIdName": this.accountInput,
        "DeliveryStatus": this.form.get('deliveryStatus').value,
        "DateFrom": formatDateOnly(this.form.get('dateFrom').value),
        "DateTo": formatDateOnly(this.form.get('dateTo').value)
      }


      this.fetchEmailDetails(searchObj);

    } else {
      this.form.markAllAsTouched();
    }
  }

  fetchEmailDetails(obj){
    this.qnsbnService.emailReportDashboard(obj).subscribe(
      res => {
        this.emailChartTableData = res;
        this.emailTableData = [ ...this.emailChartTableData.emailResponse ];
        this.deliveredPercent = parseFloat(this.emailChartTableData.percentages.deliveredPct.toFixed(2));
        this.failedPercent = this.emailTableData.length === 0 ? 0 : parseFloat((100 - this.deliveredPercent).toFixed(2));
        this.delivered.nativeElement.style.flexBasis = `${this.deliveredPercent}%`;
        this.failed.nativeElement.style.flexBasis = `${this.failedPercent}%`;
        
        // Force change detection
        this.cdr.detectChanges();
      },
      err => {
        // this.snackBarNotification.snackBarMessage('alarm', 'Please try again later');
       
      }
    );
  }

   public onDownload(): void {
    this.downloadVisible = true;
  }
  
  public onFormatSelected(format: string | null): void {
    this.downloadVisible = false;
    if (!format) {
      return; // Handle null case
    }
    try {
      if (format === 'csv') {
        this.csvData();
      } else if (format === 'xlsx') {
        this.downloadExcel(this.emailTableData, 'EmailReport');
      }
    } catch (err) {
        this.snackBarNotification.snackBarMessage('alarm', 'Please try again later');
     
    }
  }
  
  downloadExcel(data, filename) {
    const wb = XLSX.utils.book_new();
    const emailReport = data.map((row, index) => {
      const { checked, ...rest } = row;
      return { 'S.No': index + 1, ...rest };
    });
    const ws = XLSX.utils.json_to_sheet(emailReport);
    XLSX.utils.book_append_sheet(wb, ws, 'EmailReport');
    
    // Download File
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
    const blob = new Blob([this.stringToArrayBuffer(wbout)], { type: 'application/octet-stream' });

    let dwldLink = document.createElement('a');
    let url = URL.createObjectURL(blob);

    let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
    if (isSafariBrowser) {
      dwldLink.setAttribute('target', '_blank');
    }

    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.xlsx');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  stringToArrayBuffer(s) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xFF;
    }
    return buf;
  }

  downloadCSV(data, filename, headerList) {
    let csvData = this.formatCSV(data, headerList);
    let blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv;charset=utf-8;' });
    let dwldLink = document.createElement('a');
    let url = URL.createObjectURL(blob);
    let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
    if (isSafariBrowser) {
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.csv');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  formatCSV(objArray, headerList) {
    let str = '';
    str += headerList + '\r\n';

    for (let i = 0; i < objArray.length; i++) {
      let line = '';
      line += objArray[i] || '';
      str += line + '\r\n';
    }

    let res = '';
    res = 'SEP=,' + '\r\n' + str;

    return res;
  }
  
  csvData(){
    const contacts = this.emailTableData.map((row, index) => {
      const { checked, ...rest } = row;
      return { 'S.No': index + 1, ...rest };
    });

    let headers = contacts.map((item) => Object.keys(item).toString());
    headers = Array.from(new Set(headers));
    
    const values = contacts.map((item) => {
      return Object.values(item).map(value => {
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          const escapedValue = value.replace(/"/g, '""');
          return `"${escapedValue}"`;
        }
        return value;
      }).join(',');
    });
    
    this.downloadCSV(values, 'EmailReport', headers);
  }

  onCountryChange(): void {
    const selected = this.form.get('country').value || [];

    if (selected.includes('All')) {
      this.form.get('country').setValue([...this.countries]);
    } else {
      if (selected.length === 0) {
        this.form.get('country').setValue([]);
      }
    }

    this.cdr.detectChanges();
  }

  isCountryDisabled(option: string): boolean {
    return false;
  }

  onStatusChange(): void {
    const selected = this.form.get('deliveryStatus').value || [];

    if (selected.includes('All')) {
      this.form.get('deliveryStatus').setValue([...this.deliveryStatuses]);
      this.disabledStatusOptions = new Set(this.form.get('deliveryStatus').value.filter(opt => opt !== 'All'));
    } 
    
    if(!selected.includes('All')){
      const allSelected = selected.length === this.deliveryStatuses.length-1;
      const disabledOptions = this.disabledStatusOptions.size > 0;

      if (allSelected) {
        this.form.get('deliveryStatus').setValue([...this.deliveryStatuses]);
        this.disabledStatusOptions = new Set(this.form.get('deliveryStatus').value.filter(opt => opt !== 'All'));
      }

      if (disabledOptions) {
        this.form.get('deliveryStatus').reset();
        this.disabledStatusOptions.clear();
      }
    }

    this.cdr.detectChanges();
  }

  isStatusDisabled(option: string): boolean {
    return this.disabledStatusOptions.has(option);
  }

  public isAllSelected(FCN: string): boolean {
    const selected = this.form.get(FCN).value || [];
    if(FCN === 'country'){
      return selected.length === this.countries.length || selected.includes('All');
    }
    if(FCN === 'deliveryStatus'){
      return selected.length === this.deliveryStatuses.length || selected.includes('All');
    }
    return false;
  }

  public selectedRolesDisplay(FCN: string){
    if (this.isAllSelected(FCN)) {
      return 'All';
    }
    return (this.form.get(FCN).value || []).join(', ');
  }

}
